import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ReactNode,
} from "react";

import { INPUT_CLS, INPUT_FRAME_CLS } from "@/components/ui/Field";
import { RainbowUnderline } from "@/components/ui/RainbowUnderline";

import { DatePicker } from "../DatePicker";

/**
 * Filter-bar fields for the office list-page toolbars (inventory, logs, …): a
 * labelled, underline control on a transparent ground, so every page's search /
 * filter row reads identically. Compose these inside the toolbar form rather
 * than re-styling raw inputs per page ("fix once, fix everywhere"). The field
 * styles live here and nowhere else.
 */
export const FILTER_INPUT_CLS =
  "border-b border-line bg-transparent px-1 py-1 font-mono text-sm text-fg outline-none focus:border-fg";

/**
 * The house office `<textarea>`. Unlike the single-line underline fields, it's
 * a full bordered, filled box (darker `bg`, rounded) so it reads as a multi-line
 * area you type into from the top — an underline alone looks like a one-line
 * field with the caret on the line. Forwards every native textarea prop
 * (controlled or uncontrolled); use it anywhere a multi-line office input is
 * needed ("fix once, fix everywhere"). Pass `className` only for layout.
 */
export function Textarea({
  className = "",
  rows = 3,
  ...props
}: ComponentPropsWithRef<"textarea">) {
  return (
    <textarea
      {...props}
      rows={rows}
      className={`min-h-10 w-full resize-y rounded-md border border-line bg-bg px-2.5 py-2 font-mono text-sm text-fg outline-none focus:border-fg ${className}`.trim()}
    />
  );
}

// INPUT_CLS and FieldLabel live in @/components/ui/Field (shared with the
// public site); re-exported so the office `ui` barrel keeps its surface.
export { FieldLabel, INPUT_CLS, INPUT_FRAME_CLS } from "@/components/ui/Field";

/**
 * The house office single-line `<input>`: an underline field on a transparent
 * ground, matching {@link Select} and the reservation time pickers. Forwards
 * every native input prop (controlled or uncontrolled); use it for any
 * single-line office text/number/date field — never re-style a raw `<input>`
 * ("fix once, fix everywhere"). The styling lives here and nowhere else; pass
 * `className` only for width / layout.
 */
export function Input({
  className = "",
  variant = "field",
  ...props
}: ComponentPropsWithRef<"input"> & {
  /** `compact` is the small inline field — a qty in a row, a cell in an
   *  editable grid: 4px shorter (the `Select` compact's height) and sized by
   *  the caller's `className`, not `w-full`. */
  variant?: "field" | "compact";
}) {
  const look =
    variant === "compact" ? `${INPUT_FRAME_CLS} py-1` : `w-full ${INPUT_CLS}`;
  return <input {...props} className={`${look} ${className}`.trim()} />;
}

/**
 * The house file input: the native picker with its pseudo-button styled as a
 * small outline pill — `file:` pseudo-classes can't render a real
 * {@link Button}, so this mirrors `pillClass("outline", "sm")` as closely as
 * the engine allows, hover included. Forwards every native input prop
 * (`name`, `accept`, `required`, `onChange`, remount `key`, …); pass
 * `className` only for width / layout (e.g. `w-full`) — never re-style a raw
 * `<input type="file">` ("fix once, fix everywhere").
 */
export function FileInput({
  className = "",
  ...props
}: Omit<ComponentPropsWithRef<"input">, "type">) {
  return (
    <input
      type="file"
      {...props}
      className={`block border-b border-line bg-transparent px-1 py-1.5 font-mono text-xs text-fg-muted outline-none transition-colors focus:border-fg file:mr-3 file:cursor-pointer file:rounded-full file:border file:border-line file:bg-transparent file:px-3 file:py-1 file:font-mono file:text-xs file:text-fg file:transition-colors hover:file:border-fg-subtle ${className}`.trim()}
    />
  );
}

/**
 * The house office `<select>`: a native select styled as an underline field —
 * the house arrow (`.select-chevron`: one down arrow on every platform, where
 * the browser's own glyph is an up/down pair in Safari) + the editorial mono
 * spacing, matching the reservation time pickers (the reference design).
 * Forwards every native select
 * prop (controlled or uncontrolled); use it anywhere a styled office select is
 * needed — never re-style a raw `<select>` ("fix once, fix everywhere"). The
 * styling lives here and nowhere else; pass `className` only for width / layout.
 * `variant="compact"` is the select beside `FILTER_INPUT_CLS` inputs, which
 * run 4px shorter than `<Input>`.
 *
 * `busy` is for a select that ACTS on change (it saves, it navigates): pass
 * whether that is in flight and the house rainbow runs along its underline
 * until it lands. A `<select>` is a replaced element and cannot carry the
 * `::after` the rainbow is drawn with, so it rides a wrapper — drawn here,
 * once, and only when `busy` is given, so a plain form select is the bare
 * element it always was. `className` stays the select's; the wrapper hugs it,
 * and runs the full width with a `w-full` select.
 */
export function Select({
  className = "",
  variant = "field",
  busy,
  children,
  ...props
}: ComponentPropsWithoutRef<"select"> & {
  variant?: "field" | "compact";
  busy?: boolean;
}) {
  // Explicit height = its row-mates' (line-height 1.25rem + the block padding
  // + border-b): a native <select> ignores line-height and floors to a taller
  // UA height, so without this it sits a hair taller than the input beside
  // it and the two misalign when they share a row.
  const size =
    variant === "compact"
      ? "h-[calc(1.75rem+1px)] py-1"
      : "h-[calc(2rem+1px)] py-1.5";
  const select = (
    // The same 4px inset as <Input>, so their text starts on one line under
    // matching labels; the arrow sits on that inset at the other end, with
    // room kept for it so a long option never runs under it. 16px on touch:
    // iOS zooms the page when a smaller control takes focus. The office says
    // so for every field (.office-app, globals.css); this holds where the
    // select is used outside it (the public catalog's page size).
    <select
      {...props}
      className={`select-chevron ${size} cursor-pointer truncate border-b border-line bg-transparent bg-[position:right_0.25rem_center] pr-5 pl-1 font-mono text-sm text-fg outline-none focus:border-fg pointer-coarse:text-base ${className}`.trim()}
    >
      {children}
    </select>
  );
  if (busy === undefined) return select;
  return (
    <RainbowUnderline
      busy={busy}
      className={
        /(^|\s)w-full(\s|$)/.test(className)
          ? "block w-full"
          : "inline-block align-top"
      }
    >
      {select}
    </RainbowUnderline>
  );
}

/** A labelled filter input (search / date / text). Pass `className` for width.
 *
 *  Uncontrolled by default (GET-form toolbars). Client components doing
 *  instant in-page filtering (e.g. the docs index) pass `value` + `onChange`
 *  instead of `defaultValue` — same field, same styles, controlled. */
export function FilterField({
  label,
  name,
  type = "search",
  defaultValue,
  value,
  onChange,
  placeholder,
  ariaLabel,
  className = "",
}: {
  label: string;
  name: string;
  type?: "search" | "date" | "text";
  defaultValue?: string;
  /** Controlled value — pair with `onChange` (client components only). */
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="label">{label}</span>
      {type === "date" ? (
        // Dates use the house calendar popover, not the native input.
        <DatePicker
          name={name}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          ariaLabel={ariaLabel}
          className={className}
        />
      ) : (
        <input
          type={type}
          name={name}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className={`${FILTER_INPUT_CLS} ${className}`.trim()}
        />
      )}
    </label>
  );
}

/** A labelled filter select. `options` are raw values (label = value) or
 *  `{ value, label }` pairs; a leading "all" option (empty value) is included
 *  unless `allLabel` is null. */
export function FilterSelect({
  label,
  name,
  defaultValue,
  options,
  allLabel = "all",
  ariaLabel,
}: {
  label: string;
  name: string;
  defaultValue?: string;
  options: readonly (string | { value: string; label: string })[];
  allLabel?: string | null;
  ariaLabel?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="label">{label}</span>
      <Select name={name} defaultValue={defaultValue} aria-label={ariaLabel}>
        {allLabel !== null && <option value="">{allLabel}</option>}
        {options.map((o) => {
          const value = typeof o === "string" ? o : o.value;
          const text = typeof o === "string" ? o : o.label;
          return (
            <option key={value} value={value}>
              {text}
            </option>
          );
        })}
      </Select>
    </label>
  );
}

/** The box is drawn, not native: a native box's unchecked border is the
 *  browser's own gray, and it should match the other inputs (the textarea's
 *  `border-line` on `bg`, `fg` on focus). Checked fills with `fg` and cuts a
 *  check out in `bg`, so it follows the theme. Keyboard focus also draws an
 *  outline off the box: a checked box's border is already `fg`, so the border
 *  alone would show nothing. */
const CHECKBOX_CLS =
  "grid h-4 w-4 shrink-0 cursor-pointer appearance-none place-content-center rounded-[4px] border border-line bg-bg outline-none transition-colors hover:border-fg-subtle focus-visible:border-fg focus-visible:outline-solid focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-fg checked:border-fg checked:bg-fg disabled:cursor-default disabled:opacity-30 before:h-2.5 before:w-2.5 before:scale-0 before:bg-bg before:transition-transform before:content-[''] before:[clip-path:polygon(14%_44%,0_65%,50%_100%,100%_16%,80%_0%,43%_62%)] checked:before:scale-100";

/** The house checkbox. Bare by default; pass `label` for the common
 *  labelled row (mono text, like ItemForm's flag checks). `labelClassName`
 *  replaces the row's alignment, gap, and type — the base is only `flex` —
 *  for the denser rows a form draws (a triage list, a checklist that wraps
 *  and tops its box with `items-start`). `className` styles the box. */
export function Checkbox({
  label,
  className = "",
  labelClassName = "items-center gap-2 font-mono text-sm text-fg",
  ...props
}: ComponentPropsWithoutRef<"input"> & {
  label?: ReactNode;
  labelClassName?: string;
}) {
  const box = (
    <input
      type="checkbox"
      className={`${CHECKBOX_CLS} ${className}`.trim()}
      {...props}
    />
  );
  if (label == null) return box;
  return (
    <label className={`flex ${labelClassName}`}>
      {box}
      {label}
    </label>
  );
}
